import React from "react"
import { Box, Typography } from "@mui/material"
import ActionLayer, { ActionLayerButton } from "@shared/components/modals/common/ActionLayer"
import type { ServerUpdateDialogProps } from "../types/titleBarTypes"

const formatVersionLabel = (version?: string | null) => {
  const normalizedVersion = String(version || "")
    .trim()
    .replace(/^v/i, "")
  return normalizedVersion ? `v${normalizedVersion}` : "-"
}

const ServerUpdateDialog: React.FC<ServerUpdateDialogProps> = ({
  open,
  loading,
  info,
  onClose,
  onUpdate,
  updateActionEnabled = false,
  updateInProgress = false,
}) => {
  const canShowUpdateButton = Boolean(info?.updateAvailable && !info?.error && updateActionEnabled && !updateInProgress)
  const isLocalCurrentVersion = info?.currentVersionSource === "local_file"

  const actions = loading ? (
    <ActionLayerButton layerColor="cancel" onClick={onClose}>
      닫기
    </ActionLayerButton>
  ) : updateInProgress ? (
    <ActionLayerButton layerColor="cancel" onClick={onClose}>
      닫기
    </ActionLayerButton>
  ) : canShowUpdateButton ? (
    <>
      <ActionLayerButton layerColor="cancel" onClick={onClose}>
        닫기
      </ActionLayerButton>
      <ActionLayerButton onClick={onUpdate} disabled={!updateActionEnabled}>
        서버 업데이트하기
      </ActionLayerButton>
    </>
  ) : (
    <ActionLayerButton layerColor="cancel" onClick={onClose}>
      닫기
    </ActionLayerButton>
  )

  const renderContent = () => {
    if (loading) {
      return <Typography>서버 버전 정보를 확인하는 중입니다.</Typography>
    }

    if (updateInProgress) {
      return (
        <>
          {info?.currentVersion && <Typography>현재 버전 {formatVersionLabel(info.currentVersion)}</Typography>}
          {info?.latestVersion && <Typography>최신 버전 {formatVersionLabel(info.latestVersion)}</Typography>}
          <Typography>서버 업데이트를 진행 중입니다.</Typography>
          <Typography sx={{ whiteSpace: "pre-line" }}>
            서버 연결이 끊겼다가 다시 연결될 때까지 기다려주세요.
            {"\n"}업데이트가 완료되지 않으면 최대 5분 후 상태가 자동으로 초기화됩니다.
          </Typography>
        </>
      )
    }

    if (!info) {
      return <Typography>서버 버전 정보를 확인하지 못했습니다.</Typography>
    }

    if (info.error) {
      if (info.errorContext === "update_execute") {
        return (
          <>
            {info.currentVersion && <Typography>현재 버전 {formatVersionLabel(info.currentVersion)}</Typography>}
            {info.latestVersion && <Typography>최신 버전 {formatVersionLabel(info.latestVersion)}</Typography>}
            <Typography>서버 업데이트를 시작하지 못했습니다.</Typography>
            <Typography color="error.main" sx={{ whiteSpace: "pre-line" }}>
              {info.error}
            </Typography>
          </>
        )
      }

      return (
        <>
          {info.currentVersion && <Typography>현재 버전 {formatVersionLabel(info.currentVersion)}</Typography>}
          <Typography>최신 버전 정보를 확인하지 못했습니다.</Typography>
          <Typography color="error.main">{info.error}</Typography>
        </>
      )
    }

    if (info.updateAvailable) {
      return (
        <>
          <Typography>현재 버전 {formatVersionLabel(info.currentVersion)}</Typography>
          <Typography>최신 버전 {formatVersionLabel(info.latestVersion)}</Typography>
          <Typography>새로운 버전이 있습니다.</Typography>
          <Typography sx={isLocalCurrentVersion ? { whiteSpace: "pre-line" } : undefined}>
            {isLocalCurrentVersion
              ? "서버를 다운로드 합니다.\n다운로드 완료 후 PC환경에 따라 10초~2분간 차트가 정지됩니다."
              : "서버 PC에서 업데이트를 할 수 있습니다."}
          </Typography>
        </>
      )
    }

    return (
      <>
        <Typography>최신 버전 {formatVersionLabel(info.latestVersion || info.currentVersion)} 사용중입니다.</Typography>
        <Typography>업데이트할 항목이 없습니다.</Typography>
      </>
    )
  }

  return (
    <ActionLayer open={open} onClose={onClose} title="서버 업데이트" actions={actions} maxWidth="xs">
      <Box sx={{ minWidth: 320, display: "flex", flexDirection: "column", gap: 1.25 }}>{renderContent()}</Box>
    </ActionLayer>
  )
}

export default ServerUpdateDialog

